/**
 * composeBuffer.js — lifetime control for the compose scratch buffer in localStorage.
 *
 * ComposeEmail.js mirrors the compose window into localStorage as the user types: `sendingEmail` is the
 * mail BODY, rewritten on every keystroke, and `recipient` / `topic` / `cc` / `bcc` are the addressing and
 * subject line. The reply/forward modal does the same through `replyEmail`. All of it is user content, all
 * of it in cleartext, and localStorage is durable — so an abandoned compose used to sit readable on disk
 * indefinitely, long after the window was closed and after the browser was shut down.
 *
 * WHY CLEARED RATHER THAN SEALED UNDER THE DEK
 * --------------------------------------------
 * Sealing would only be worth its cost if the buffer were a durable recovery copy. It is not — the app
 * already throws it away on the next load, so there is nothing to protect across sessions:
 *
 *   - `isCompose` lives in redux (store.js) and is not persisted, so ANY page load closes the compose
 *     window. The buffer is never read back into a reopened compose across a load.
 *   - Reopening compose from the Navbar button blanks recipient/topic/cc/bcc and calls singnature(), which
 *     overwrites `sendingEmail` outright. Whatever survived the restart is destroyed before it is shown.
 *
 * So the persisted copy has no reader and no value: it is pure residue. Clearing it costs the user nothing
 * and removes the plaintext, whereas sealing it would buy nothing and cost a lot. localStorage is
 * synchronous and the DEK resolves asynchronously, so a sealed buffer would need every read site
 * (including a React useState initializer and two Swal predicates) to become async, plus a debounced write
 * and a hydrate-before-first-keystroke handshake. Get that handshake wrong — hydration lands after the user
 * starts typing, or a debounced write fires before hydration — and you overwrite the body with an empty
 * string. That is a real data-loss vector traded for a buffer the app discards anyway.
 *
 * The durable copy of a compose the user wants to keep is the DRAFT row, which IS sealed under the DEK
 * (db/at-rest.js). This buffer is the live editor's scratch space and now lives exactly as long as the
 * editor is on screen.
 *
 * Cleared on: send, discard, save-to-draft, closing compose without saving, page unload, and app boot
 * (which covers a crash or a browser killed mid-compose, where unload never fired). Sign-out and sign-in
 * already localStorage.clear().
 */

/** Every localStorage key the COMPOSE window (ComposeEmail.js) writes user content to. */
export const COMPOSE_BUFFER_KEYS = ['sendingEmail', 'recipient', 'topic', 'cc', 'bcc'];

/**
 * The reply/forward modal (modals/Editor.js + modals/common-alert.js) keeps its own buffer, `replyEmail` —
 * the same leak by the same mechanism: the mail body, rewritten on every keystroke, durable, and read back
 * on mount. It gets the same session-scoped lifetime.
 *
 * It is a SEPARATE list on purpose. The reply modal and the compose window are independent and can be open
 * at the same time, and — exactly like the bfcache case in installComposeBufferScrub — common-alert.js
 * sends the body it reads from localStorage, not from React state. So compose finishing a send must not
 * scrub the reply modal's buffer out from under it, or the reply would go out empty. Only the boot/unload
 * scrub, where nothing is mid-send, touches both.
 */
export const REPLY_BUFFER_KEYS = ['replyEmail'];

const ALL_EDITOR_BUFFER_KEYS = [...COMPOSE_BUFFER_KEYS, ...REPLY_BUFFER_KEYS];

/**
 * removeItem, not setItem(key, ''): an empty string is still a stored value. Every read site coerces with
 * `|| ''` or tests falsiness, so a missing key and an empty string are indistinguishable to them.
 */
function removeKeys(keys) {
  try {
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    /* storage disabled/full — never break the caller over a scrub */
  }
}

/** Drop the compose window's buffer. Scoped to compose: see REPLY_BUFFER_KEYS. */
export function clearComposeBuffer() {
  removeKeys(COMPOSE_BUFFER_KEYS);
}

/**
 * Drop the reply/forward buffer. The compose window clears itself after a send; the reply modal had no
 * equivalent, so a sent reply's cleartext body stayed in localStorage for the rest of the session — and
 * survived a bfcache freeze, since the pagehide scrub deliberately skips persisted pages. Call this once
 * the send has read the body and succeeded.
 */
export function clearReplyBuffer() {
  removeKeys(REPLY_BUFFER_KEYS);
}

/**
 * Scrub at boot and on unload, so the buffer cannot outlive the session.
 *
 * `pagehide` rather than `beforeunload`: it is the reliable one on mobile and in the PWA, and it does not
 * risk an unload prompt.
 *
 * The `event.persisted` guard is load-bearing, NOT a micro-optimisation. pagehide also fires when the page
 * is merely FROZEN into the bfcache — an app-switch or screen-lock on mobile — and such a page can be
 * restored later with its JS heap intact: the compose window is still open and the editor still shows the
 * text. But ComposeEmail reads the body it actually SENDS from localStorage, not from React state
 * (`sendEmails(..., localStorage.getItem("sendingEmail"), ...)`), so scrubbing on a freeze would mean the
 * user returns to a full-looking editor and silently sends an EMPTY mail. persisted === true means "may
 * come back" → leave it alone; persisted === false means the document is being discarded → scrub.
 *
 * A frozen page that is later evicted without ever being restored keeps its buffer, which is exactly what
 * the boot-time clear is for. That clear is the important half anyway: it is what covers the browser being
 * closed or killed mid-compose, where no unload handler ever runs.
 */
export function installComposeBufferScrub() {
  removeKeys(ALL_EDITOR_BUFFER_KEYS);
  window.addEventListener('pagehide', (event) => {
    if (event?.persisted) return;
    removeKeys(ALL_EDITOR_BUFFER_KEYS);
  });
}
